"use client";
import { useReducer, useMemo, useState, useCallback, useEffect } from "react";

import {
  DocumentNode,
  OperationVariables,
  QueryLazyOptions,
  useLazyQuery,
} from "@apollo/client";
import { useIsAuthenticated } from "@azure/msal-react";
import { useParams, usePathname, useSearchParams, useRouter } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import {
  BloomreachSearchByKeywordResponse,
  BloomreachSearchByKeywordVariables,
} from "~/bff/transport/BloomreachSearchByKeyword";
import { Store } from "~/bff/types/Store";
import { getSortOrderForGA, updateUrlHash } from "~/components/plp-page/helpers";
import { getFilterTypeAndValueGA } from "~/components/plp-page/hooks/use-request-handler/helpers/ga-apply-filter-event";
import { getPageNumberFromMap } from "~/components/plp-page/hooks/use-request-handler/helpers/get-page-number-from-map";
import {
  queryToRemoveClickCollectExclusive,
  shopFilterPrepareQuery,
} from "~/components/products-layout/components/products-filters/components/filter/components/shop-filter/helpers/shop-filter-prepare-query";
import { mappedFacetsRangesForFilter } from "~/components/rbp-page/helpers";
import { SearchTabs } from "~/components/search-results-page/types";
import {
  DataForGaTypes,
  DataLayerData,
  DataLayerDataTypes,
  EventsNames,
  EventTypes,
} from "~/constants/data-layer";
import { Params } from "~/constants/request";
import { getApolloClient } from "~/graphql/client";
import { getCountryCode } from "~/helpers/country/get-country-code";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { updatePageNumberOnUrl } from "~/helpers/update-page-number-on-url";
import { removePageFromUrl } from "~/helpers/use-remove-page-from-url/helper";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { Nullable } from "~/types/general.types";
import {
  IHandlerParams,
  IHashFilterParams,
  IOnCompletedPayload,
  IUseRequestHandlerActionsKind,
  ProductRequestType,
} from "~/types/products-request-handler";

import { getNewItems } from "./helpers/get-new-items";
import { handleLoadMore } from "./helpers/handle-load-more";
import { handleLoadPrevious } from "./helpers/handle-load-previous";
import { onActiveSortingParamSet } from "./helpers/on-active-sorting-param-set";
import { requestReducer } from "./reducers/request-reducer";
import { IUseRequestHandlerReturn, IUseRequestHandler } from "./types";

export const useSrpProductsRequestHandler = (
  query: DocumentNode,
  initialState: IUseRequestHandler,
  {
    q,
    brUid2,
    brRefUrl,
  }: Partial<BloomreachSearchByKeywordVariables & OperationVariables>,
): IUseRequestHandlerReturn<BloomreachSearchByKeywordVariables> => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { locale } = useParams<GlobalPathParams>();
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();
  const client = useMemo(() => getApolloClient(), []);

  const currentTab = useMemo(
    () => (searchParams.get(Params.TAB) as SearchTabs) ?? SearchTabs.PRODUCTS,
    [searchParams],
  );

  const [data, dispatchRequest] = useReducer(requestReducer, initialState);

  const [currentStore, setCurrentStore] = useState<Nullable<Store>>(null);
  const [isShopFilter, setShopFilter] = useState<boolean | null>(null);
  const [shopFilterSortingParam, setShopFilterSortingParam] =
    useState<ProductRequestType>(ProductRequestType.AllProducts);
  const [numFoundGA, setNumFoundGA] =
    useState<Nullable<undefined | number>>(undefined);
  const [shouldTriggerGAFilterEvent, setShouldTriggerGAFilterEvent] =
    useState<boolean>(false);
  const [GAFilter, setGAFilter] = useState<Nullable<string>>("");

  const {
    handlerParams: {
      activeSortingParam,
      appliedFilters,
      itemsMap,
      pageSize,
      direction,
      currentPageNumber,
      productsMarkerId,
      setProductsMarkerId,
    },
    items,
  } = data;

  const handlerParams: IHandlerParams = {
    activeSortingParam,
    appliedFilters,
    direction,
    isShopFilter,
    currentStore,
    itemsMap,
    pageSize,
    currentPageNumber,
    productsMarkerId,
    setProductsMarkerId,
  };

  const [getMoreItems, { loading: isLoading, variables }] = useLazyQuery<
    BloomreachSearchByKeywordResponse,
    BloomreachSearchByKeywordVariables & OperationVariables
  >(query, {
    notifyOnNetworkStatusChange: true,
    client,
    onCompleted: ({ bloomreachSearchByKeyword }) => {
      if (currentTab === SearchTabs.PRODUCTS) {
        const { response, facet_counts, keywordRedirect } =
          bloomreachSearchByKeyword ?? {};

        const { docs: loadedItems = [], numFound = 0 } = response ?? {};
        const { facet_fields: facetFields = [], facet_ranges: facetRanges = [] } =
          facet_counts ?? {};

        if (keywordRedirect?.url) {
          location.replace(keywordRedirect.url);
        }
        const newItems = getNewItems(
          variables?.shouldConcat,
          direction,
          loadedItems,
          items,
        );

        const pagesArray = Array.from(itemsMap.values());
        const maxPage = pagesArray[pagesArray.length - 1];
        const minPage = getPageNumberFromMap(itemsMap);
        const currentPage =
          direction === "next"
            ? maxPage
            : direction === "previous"
              ? minPage
              : currentPageNumber.current;

        const payload: IOnCompletedPayload = {
          itemsMap,
          filters: {
            ...mappedFacetsRangesForFilter(facetFields),
            ...mappedFacetsRangesForFilter(facetRanges),
          },
          numFound,
          loadedItems,
          newItems,
          currentPage,
          shouldConcat: variables?.shouldConcat,
          direction: direction,
          setNumFound: setNumFoundGA,
          setShouldTriggerGAFilterEvent,
          isUiLoading: false,
        };
        if (direction) {
          if (direction === "next") {
            currentPageNumber.current = maxPage + 1;
          } else if (direction === "previous") {
            currentPageNumber.current = minPage - 1;
          }

          updatePageNumberOnUrl({
            pageNumber: currentPageNumber.current,
            searchParams,
            pathname,
            router,
          });
        }
        dispatchRequest({
          type: IUseRequestHandlerActionsKind.UPDATE_ON_COMPLETED,
          payload,
        });
      }
    },
  });
  const config = useAzureConfigurator(locale);
  const isClickAndCollectEnabled: boolean = useMemo(
    () => (config?.featureFlags?.clickAndCollect?.enabled ? true : false),
    [config],
  );

  const isClickAndCollectQuery = useCallback(() => {
    return (
      isShopFilter && currentStore?.clickAndCollectStore && isClickAndCollectEnabled
    );
  }, [currentStore?.clickAndCollectStore, isClickAndCollectEnabled, isShopFilter]);

  const efq = useMemo(
    () =>
      isClickAndCollectQuery()
        ? shopFilterPrepareQuery(shopFilterSortingParam)
        : queryToRemoveClickCollectExclusive(isClickAndCollectEnabled),
    [isClickAndCollectEnabled, isClickAndCollectQuery, shopFilterSortingParam],
  );

  const getParams: () => IHashFilterParams = useCallback(
    () => ({
      activeSortingParam,
      appliedFilters,
      isShopFilter: !!isShopFilter,
      selectedStoreId: currentStore?.id,
      productType: shopFilterSortingParam,
      config: {
        pageNumber: currentPageNumber.current,
        getMoreItems: getMoreItems as unknown as (
          options?: QueryLazyOptions<BloomreachSearchByKeywordVariables>,
        ) => void,
        variables: {
          locale,
          efq,
          q,
          brUid2,
          brRefUrl,
        },
      },
    }),
    [
      activeSortingParam,
      appliedFilters,
      isShopFilter,
      currentStore?.id,
      shopFilterSortingParam,
      currentPageNumber,
      getMoreItems,
      locale,
      efq,
      q,
      brUid2,
      brRefUrl,
    ],
  );

  const handleShopFilterClick = useCallback(
    (store: Nullable<Store>) => {
      const params = {
        ...getParams(),
        isShopFilter: !isShopFilter,
        selectedStoreId: store?.id,
      };

      currentPageNumber.current = 1;
      setShopFilter(!isShopFilter);
      setCurrentStore(store);
      if (params?.selectedStoreId) {
        updateUrlHash({
          ...params,
          isShopFilter: !isShopFilter,
          config: {
            ...params.config,
            variables: {
              ...params.config.variables,
              efq: isClickAndCollectEnabled ? null : efq,
            },
          },
        });
      }
      removePageFromUrl(searchParams, pathname);
    },
    [
      getParams,
      isShopFilter,
      currentPageNumber,
      searchParams,
      pathname,
      isClickAndCollectEnabled,
      efq,
    ],
  );

  const handleUpdateStore = useCallback(
    (newStore: Nullable<Store>) => {
      let params: IHashFilterParams = {
        ...getParams(),
        isShopFilter: true,
        selectedStoreId: newStore?.id,
      };

      if (
        (!currentStore?.clickAndCollectStore || !newStore?.clickAndCollectStore) &&
        isClickAndCollectEnabled
      ) {
        const newEfq = isClickAndCollectQuery()
          ? shopFilterPrepareQuery(ProductRequestType.AllProducts)
          : null;

        setShopFilterSortingParam(ProductRequestType.AllProducts);
        params = {
          ...params,
          productType: ProductRequestType.AllProducts,
          config: {
            ...params.config,
            variables: {
              ...params.config.variables,
              efq: newEfq,
            },
          },
        };
      }

      currentPageNumber.current = 1;
      setShopFilter(true);
      setCurrentStore(newStore);
      updateUrlHash({ ...params });
      removePageFromUrl(searchParams, pathname);
    },
    [
      getParams,
      currentStore?.clickAndCollectStore,
      isClickAndCollectEnabled,
      currentPageNumber,
      searchParams,
      pathname,
      isClickAndCollectQuery,
    ],
  );

  const onShopFilterDropdown = useCallback(
    (value: ProductRequestType) => {
      const params = getParams();
      const newEfq = isClickAndCollectQuery() ? shopFilterPrepareQuery(value) : null;

      currentPageNumber.current = 1;
      setShopFilterSortingParam(value);
      updateUrlHash({
        ...params,
        productType: value,
        config: {
          ...params.config,
          variables: {
            ...params.config.variables,
            efq: newEfq,
          },
        },
      });
      removePageFromUrl(searchParams, pathname);
    },
    [currentPageNumber, getParams, isClickAndCollectQuery, pathname, searchParams],
  );

  const handleShopFilterChipClick = useCallback(() => {
    const params = getParams();
    const newEfq = isClickAndCollectQuery()
      ? shopFilterPrepareQuery(ProductRequestType.AllProducts)
      : null;

    currentPageNumber.current = 1;
    setShopFilterSortingParam(ProductRequestType.AllProducts);
    updateUrlHash({
      ...params,
      productType: ProductRequestType.AllProducts,
      config: {
        ...params.config,
        variables: {
          ...params.config.variables,
          efq: newEfq,
        },
      },
    });
    removePageFromUrl(searchParams, pathname);
  }, [currentPageNumber, getParams, isClickAndCollectQuery, pathname, searchParams]);

  const getDataForGA: (params: DataForGaTypes) => DataLayerDataTypes = useCallback(
    (params) => {
      const country = getCountryCode(locale);
      const { filterValue, filterType, numFound, sortOrder } = params || {};

      return {
        [DataLayerData.event]: EventsNames.plpApplyFilter,
        [DataLayerData.siteCountry]: country,
        [DataLayerData.type]: EventTypes.applyFilter,
        [DataLayerData.pageCategory]: "Search",
        [DataLayerData.pageSubCategory]: null,
        [DataLayerData.pageSubSubCategory]: null,
        [DataLayerData.loggedInStatus]: isAuthenticated,
        [DataLayerData.storeSelected]: currentStore?.geomodifier,
        [DataLayerData.filterType]: filterType,
        [DataLayerData.filterValue]: filterValue,
        [DataLayerData.filterResults]: numFound,
        [DataLayerData.sortOrder]: sortOrder,
      };
    },
    [locale, isAuthenticated, currentStore?.geomodifier],
  );

  useEffect(() => {
    const { appliedFilterOnClick, GAdata, activeSortingParam } =
      data?.handlerParams || {};

    const sortOrder = getSortOrderForGA(activeSortingParam);
    if (appliedFilterOnClick) {
      const { filterType, filterValue } = getFilterTypeAndValueGA(
        appliedFilterOnClick,
        locale,
      );

      if (!appliedFilterOnClick?.[0] || GAFilter === appliedFilterOnClick?.[0]) {
        setShouldTriggerGAFilterEvent(false);
      }

      if (
        appliedFilterOnClick?.[0] &&
        shouldTriggerGAFilterEvent &&
        GAFilter !== appliedFilterOnClick?.[0] &&
        filterType &&
        filterValue &&
        sortOrder &&
        numFoundGA
      ) {
        const dataForGA = getDataForGA({
          filterType,
          filterValue,
          breadcrumbs: GAdata?.breadcrumbs,
          sortOrder,
          numFound: numFoundGA,
        });

        pushDataLayer(dataForGA);
        setShouldTriggerGAFilterEvent(false);
        setGAFilter(appliedFilterOnClick?.[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.handlerParams?.GAdata?.breadcrumbs,
    data?.handlerParams?.appliedFilterOnClick,
    numFoundGA,
    shouldTriggerGAFilterEvent,
  ]);

  return {
    efq,
    data,
    isLoading,
    isShopFilter,
    currentStore,
    setShopFilter,
    setCurrentStore,
    handleShopFilterChipClick,
    handleShopFilterClick,
    handleUpdateStore,
    getMoreItems,
    isUiLoading: data?.handlerParams?.isUiLoading,
    dispatchRequest,
    shopFilterSortingParam,
    setShopFilterSortingParam,
    onShopFilterDropdown,
    handleLoadMore: async (): Promise<void> =>
      handleLoadMore({
        locale,
        q,
        efq,
        brUid2,
        brRefUrl,
        getMoreItems,
        dispatchRequest,
        ...handlerParams,
      }),
    handleLoadPrevious: async (): Promise<void> =>
      handleLoadPrevious({
        locale,
        q,
        efq,
        brUid2,
        brRefUrl,
        getMoreItems,
        dispatchRequest,
        ...handlerParams,
      }),
    onActiveSortingParamSet: async (sortingParameter: string): Promise<void> =>
      onActiveSortingParamSet({
        q,
        efq,
        searchParams,
        pathname,
        locale,
        productType: shopFilterSortingParam,
        sortingParameter,
        getMoreItems,
        dispatchRequest,
        appliedFilters,
        isShopFilter,
        currentStore,
        pageNumber: currentPageNumber,
        productsMarkerId,
        setProductsMarkerId,
        brUid2,
        brRefUrl,
      }),
  };
};
